/**
 * LoginPage Component
 * Professional, mobile-friendly login page for the Oath authentication flow
 * Includes splash screen check that blocks login during maintenance windows
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
  ArrowsRightLeftIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useOathAuth } from '../../services/oathAuthContext';
import { checkSplashScreen } from '../../services/oathSplashScreenService';
import { DualRingSpinner, SimpleSpinner } from '../ui/Spinner';

const BYPASS_PASSWORD = 'r2d2';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading: authLoading } = useOathAuth();

  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Splash screen state
  const [splashActive, setSplashActive] = useState(false);
  const [splashData, setSplashData] = useState(null);
  const [splashBypassed, setSplashBypassed] = useState(false);
  const [showBypassModal, setShowBypassModal] = useState(false);
  const [bypassInput, setBypassInput] = useState('');
  const [bypassError, setBypassError] = useState('');

  // Check for active splash screen on mount
  useEffect(() => {
    const checkSplash = async () => {
      const result = await checkSplashScreen('p');
      if (result.success && result.active && result.splashScreen) {
        setSplashActive(true);
        setSplashData(result.splashScreen);
      }
    };
    checkSplash();
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      // After session expiry, always go to dashboard instead of the previous page
      const sessionExpired = sessionStorage.getItem('oathSessionExpired');
      if (sessionExpired) {
        sessionStorage.removeItem('oathSessionExpired');
      }
      const fromLocation = !sessionExpired ? location.state?.from : null;
      const from = fromLocation ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}` : '/app/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!loginId.trim()) {
      setError('Please enter your username');
      return;
    }

    if (!password) {
      setError('Please enter your password');
      return;
    }

    setIsSubmitting(true);

    const result = await login(loginId.trim(), password);

    if (!result.success) {
      setError(result.error || 'Login failed');
      setIsSubmitting(false);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleBypassSubmit = (e) => {
    e.preventDefault();
    if (bypassInput === BYPASS_PASSWORD) {
      setSplashBypassed(true);
      setShowBypassModal(false);
      setBypassInput('');
      setBypassError('');
    } else {
      setBypassError('Incorrect password');
    }
  };

  const handleCloseBypassModal = () => {
    setShowBypassModal(false);
    setBypassInput('');
    setBypassError('');
  };

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
        <div className="text-center">
          <DualRingSpinner size="lg" className="mx-auto" />
          <p className="mt-4 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show splash screen blocking page
  if (splashActive && !splashBypassed) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-600 via-primary to-primary-700 relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-900/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        </div>

        {/* Mobile Header */}
        <div className="lg:hidden py-5 px-4 bg-white border-b border-gray-100 relative z-10">
          <div className="flex flex-col items-center gap-1">
            <img src="/bankers-surety-logo.png" alt="Bankers Surety" className="h-9" />
            <span className="text-gray-500 text-xs font-medium">Agency Portal</span>
          </div>
        </div>

        <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12 relative z-10">
          <div className="w-full max-w-lg">
            {/* Desktop logo */}
            <div className="hidden lg:flex justify-center mb-8">
              <img src="/bankers-surety-logo.png" alt="Bankers Surety" className="h-12 brightness-0 invert" />
            </div>

            {/* Splash Card */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/20">
              <div className="h-1.5 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400"></div>
              <div className="p-6 sm:p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
                  </div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {splashData.title}
                  </h1>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 mb-6">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{splashData.message}</p>
                </div>

              </div>
            </div>

            {/* Help Text */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                <span className="text-sm text-primary-100">Need assistance?</span>
                <a
                  href="tel:877-778-5245"
                  className="text-sm text-white font-semibold hover:text-primary-50 transition-colors"
                >
                  877-778-5245
                </a>
              </div>
            </div>
          </div>
        </main>

        <footer className="py-5 px-4 text-center relative z-10">
          <p className="text-xs text-primary-200/80">
            <button
              type="button"
              onClick={() => setShowBypassModal(true)}
              className="align-baseline rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
              aria-label="Copyright notice"
            >
              &copy;
            </button>{' '}
            {new Date().getFullYear()} Bankers Surety. All rights reserved.
          </p>
        </footer>

        {showBypassModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-gray-900/60 backdrop-blur-sm">
            <button
              type="button"
              className="absolute inset-0 w-full h-full cursor-default"
              aria-label="Close access dialog"
              onClick={handleCloseBypassModal}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="bypass-dialog-title"
              className="relative w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl ring-1 ring-black/10"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 id="bypass-dialog-title" className="text-lg font-semibold text-gray-900">
                    Authorized Access
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Enter the access code to continue.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCloseBypassModal}
                  className="rounded-lg px-2 py-1 text-sm font-semibold text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  aria-label="Close"
                >
                  x
                </button>
              </div>

              <form onSubmit={handleBypassSubmit} className="mt-5 space-y-3">
                <input
                  type="password"
                  value={bypassInput}
                  onChange={(e) => setBypassInput(e.target.value)}
                  placeholder="Access code"
                  autoFocus
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-gray-400"
                />
                {bypassError && (
                  <p className="text-xs text-red-600">{bypassError}</p>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleCloseBypassModal}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
                  >
                    Continue
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Panel - Branding (hidden on mobile, shown on lg+) */}
      <div className="hidden lg:flex lg:w-1/2 bg-white relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-50 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        </div>

        {/* Top accent bar using brand colors */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-primary to-orange-500"></div>

        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12">
          {/* Original logo with brand colors */}
          <img
            src="/bankers-surety-logo.png"
            alt="Bankers Surety"
            className="h-14 mb-6"
          />
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary-50 rounded-full mb-4 border border-primary-100">
            <span className="text-xs font-semibold text-primary uppercase tracking-wider">
              Agency Portal
            </span>
          </div>
          <p className="text-gray-600 text-center text-lg max-w-md">
            Your trusted platform for managing transfer bonds, large bonds, and power reporting.
          </p>

          {/* Feature highlights with brand-colored accents */}
          <div className="mt-10 space-y-3 w-full max-w-sm">
            <div className="flex items-center gap-4 p-4 bg-primary-50/50 rounded-xl border border-primary-100">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <ArrowsRightLeftIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-gray-900 font-medium text-sm">Transfer Bonds</p>
                <p className="text-gray-500 text-xs">Request and post with ease</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-orange-50/50 rounded-xl border border-orange-100">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <ChartBarIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-gray-900 font-medium text-sm">Power Reporting</p>
                <p className="text-gray-500 text-xs">Track and submit in real-time</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-primary-50/50 rounded-xl border border-primary-100">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <ShieldCheckIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-gray-900 font-medium text-sm">Secure Access</p>
                <p className="text-gray-500 text-xs">Enterprise-grade security</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex flex-col bg-gradient-to-br from-primary-600 via-primary to-primary-700 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-900/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>

        {/* Mobile Header with Original Logo */}
        <div className="lg:hidden py-5 px-4 bg-white border-b border-gray-100 relative z-10">
          <div className="flex flex-col items-center gap-1">
            <img
              src="/bankers-surety-logo.png"
              alt="Bankers Surety"
              className="h-9"
            />
            <span className="text-gray-500 text-xs font-medium">Agency Portal</span>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12 relative z-10">
          <div className="w-full max-w-sm sm:max-w-md">
            {/* Branding */}
            <div className="text-center mb-8 sm:mb-10">
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight drop-shadow-sm">
                Welcome Back
              </h1>
              <p className="text-sm sm:text-base text-primary-100 mt-2">
                Sign in to your account
              </p>
            </div>

            {/* Login Card */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/20">
              {/* Orange accent bar */}
              <div className="h-1 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400"></div>
              <div className="p-6 sm:p-8 lg:p-10">
              {/* Error Alert */}
              {error && (
                <div className="mb-5 p-3 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-sm text-red-700 text-center">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {/* Username Field */}
                <div className="mb-5">
                  <label
                    htmlFor="loginId"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Username
                  </label>
                  <input
                    id="loginId"
                    type="text"
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                    disabled={isSubmitting}
                    autoComplete="username"
                    autoFocus
                    placeholder="Enter your username"
                    className="w-full px-4 py-3.5 text-base sm:text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 placeholder:text-gray-400"
                  />
                </div>

                {/* Password Field */}
                <div className="mb-6">
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isSubmitting}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      className="w-full px-4 py-3.5 pr-12 text-base sm:text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 placeholder:text-gray-400"
                    />
                    <button
                      type="button"
                      onClick={handleTogglePassword}
                      disabled={isSubmitting}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 active:text-gray-800 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 transition-all duration-200"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center shadow-lg shadow-primary/30 hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  {isSubmitting ? (
                    <>
                      <SimpleSpinner size="sm" className="text-white mr-2" />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>

                {/* Forgot Password Link */}
                <div className="mt-5 text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-sm text-gray-500 hover:text-primary font-medium transition-colors"
                  >
                    Forgot your password?
                  </button>
                </div>

                {/* Register Link */}
                <div className="mt-3 text-center">
                  <span className="text-sm text-gray-400">New agency? </span>
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className="text-sm text-primary hover:text-primary-dark font-semibold transition-colors"
                  >
                    Register here
                  </button>
                </div>
              </form>
              </div>
            </div>

            {/* Help Text */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                <span className="text-sm text-primary-100">Need assistance?</span>
                <a
                  href="tel:877-778-5245"
                  className="text-sm text-white font-semibold hover:text-primary-50 transition-colors"
                >
                  877-778-5245
                </a>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-5 px-4 text-center relative z-10">
          <p className="text-xs text-primary-200/80">
            &copy; {new Date().getFullYear()} Bankers Surety. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default LoginPage;
